import { createServerFn } from "@tanstack/solid-start";
import { z } from "zod";
import { withSentrySpan } from "~/lib/sentry-span";
import { authFunctionMiddleware } from "~/server/middleware/auth";

export const GetBankTransactionsPageDataInputSchema = z.object({
  businessId: z.string().trim().min(1),
});

/**
 * Aggregate server function for the bank transactions index page.
 *
 * Fetches bank transactions list and business detail in a single HTTP round
 * trip on client-side navigation.
 */
export const getBankTransactionsPageData = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => GetBankTransactionsPageDataInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("bankTransactions.pageData", async () => {
      const [businessMod, bankTransactionMod] = await Promise.all([
        import("../business/business.server"),
        import("./bank-transaction.server"),
      ]);
      const { getBusinessDetailForUser } = businessMod;
      const {
        listBankTransactionsForUser,
        listBankTransactionCategoriesForUser,
      } = bankTransactionMod;

      const [businessDetail, bankTransactions, categories] = await Promise.all([
        getBusinessDetailForUser(context.user.id, data.businessId),
        listBankTransactionsForUser(context.user.id, data.businessId, {
          limit: 20,
          // Match the table's default sort so the seeded cache entry uses the
          // same query key the table reads — otherwise the table misses the
          // seed and flashes empty while it refetches.
          sortBy: "date",
          sortDir: "desc",
        }),
        // Category facets power the toolbar's facet filter. Without seeding
        // them here the facet query fires cold on first mount after a hard
        // refresh — the only unseeded query on this route — and the undefined
        // data suspends the nearest Suspense boundary (one-time flash).
        listBankTransactionCategoriesForUser(context.user.id, data.businessId),
      ]);

      return { businessDetail, bankTransactions, categories } as const;
    });
  });
